import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export type RefundStatus = "pending" | "approved" | "denied" | "processed";
export type RefundReferenceKind = "alacarte" | "subscription" | "other";

export interface RefundRequest {
  id: string;
  user_id: string;
  reference_kind: RefundReferenceKind;
  reference_id: string | null;
  amount_cents: number | null;
  currency: string | null;
  reason: string;
  status: RefundStatus;
  reviewer_notes: string | null;
  stripe_refund_id: string | null;
  created_at: string;
  updated_at: string;
}

export const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  pending: "Pending review",
  approved: "Approved",
  denied: "Denied",
  processed: "Refunded",
};

export function useRefunds() {
  const { user } = useAuth();
  const [refunds, setRefunds] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setRefunds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("refund_requests")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setRefunds((data as RefundRequest[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(
    async (input: {
      reference_kind: RefundReferenceKind;
      reference_id?: string;
      amount_cents?: number;
      reason: string;
    }) => {
      if (!user) return { error: "not_signed_in" };
      const { error } = await supabase.from("refund_requests").insert({
        user_id: user.id,
        reference_kind: input.reference_kind,
        reference_id: input.reference_id ?? null,
        amount_cents: input.amount_cents ?? null,
        reason: input.reason,
      });
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [user, load],
  );

  return { refunds, loading, submit, reload: load };
}
